import { useEffect, useState } from 'react';
import type { MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { MiDocumento } from '../api/ops/documentosCliente';
import { ErrorApi } from '../api/problema';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { navegador } from '../logica/navegador';
import { BotonPc, Pagina } from './Pagina';
import { direccionDeVuelta, plazo } from './firma';
import './firma.css';

/**
 * «Mis documentos» (9.12, `/mis-documentos`): con el token de cliente de «Mis datos», el negocio a elegir entre sus perfiles
 * y sus documentos ahí (`listMyDocuments`), cada uno con su estado. Firmado: «Descargar el PDF firmado» (dirección de 5
 * minutos, `getMySignedDocumentFile`). Pendiente y `can_sign`: «Leer y firmar» (`startMySigningSession`, vuelve aquí).
 * Marca de Reservaria con el nombre del negocio, como la página para firmar (G798). Los enlaces que llegan aquí llevan el
 * negocio (`?negocio=<business_id>`, G854): se abre directamente su perfil.
 */
interface Props {
    ops: OperacionesClienteFinal;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    onSalir: () => void;
    onCaducada: () => void;
    abrirDescarga?: (url: string) => void;
    /** El negocio del enlace (`?negocio=`, G854): su perfil, si lo tiene, sale elegido. */
    negocio?: string | null;
}

const fecha = (iso: string | null | undefined) => (iso == null ? '' : new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }));

const ESTADO: Readonly<Record<MiDocumento['status'], { chip: string; estado: EstadoChip; icono: NombreIcono; fondo: string; tinta: string }>> = {
    requested: { chip: 'Pendiente de firmar', estado: 'pending', icono: 'signature', fondo: 'var(--rv-warning-soft)', tinta: 'var(--rv-warning-ink)' },
    pending: { chip: 'Fírmalo en el centro', estado: 'pending', icono: 'signature', fondo: 'var(--rv-warning-soft)', tinta: 'var(--rv-warning-ink)' },
    signed: { chip: 'Firmado', estado: 'confirmed', icono: 'file-check', fondo: 'var(--rv-ink)', tinta: '#fff' },
    voided: { chip: 'Anulado', estado: 'problem', icono: 'file-text', fondo: 'var(--rv-paper)', tinta: 'var(--rv-fg-3)' },
};

function linea(d: MiDocumento): string {
    switch (d.status) {
        case 'signed':
            return `Firmado el ${fecha(d.signed_at)}`;
        case 'requested':
            return `Fírmalo antes del ${plazo(d.sign_by)}`;
        case 'pending':
            return 'Fírmalo en el centro';
        default:
            return 'Anulado';
    }
}

export function MisDocumentos({ ops, onSalir, onCaducada, abrirDescarga = navegador.abrir, negocio = null }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [elegido, setElegido] = useState<string | null>(null);
    const [docs, setDocs] = useState<{ perfil: string; lista: MiDocumento[] } | null>(null);
    const [error, setError] = useState(false);
    const [intento, setIntento] = useState(0);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState<string | null>(null);
    const [nota, setNota] = useState<{ id: string; texto: string } | null>(null);

    const tratar = (e: unknown, si: () => void) => {
        if (e instanceof ErrorApi && e.estado === 401) {
            onCaducada();
        } else {
            si();
        }
    };

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (vivo) {
                    setPerfiles(l.data.filter((p) => p.ended == null));
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    tratar(e, () => { setError(true); });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.find((p) => p.business.id === negocio) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        ops.listMyDocuments(idPerfil, { limit: 50 })
            .then((l) => {
                if (vivo) {
                    setDocs({ perfil: idPerfil, lista: l.data });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    tratar(e, () => { setError(true); });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, intento]);

    const descargar = (d: MiDocumento) => {
        if (idPerfil === null) {
            return;
        }
        setOcupado(d.id);
        ops.getMySignedDocumentFile(idPerfil, d.id)
            .then((f) => {
                abrirDescarga(f.url);
                setNota({ id: d.id, texto: 'El enlace de descarga dura 5 minutos. Guárdalo en un sitio seguro.' });
            })
            .catch((e: unknown) => { tratar(e, () => { setNota({ id: d.id, texto: 'No hemos podido preparar la descarga. Prueba de nuevo.' }); }); })
            .finally(() => { setOcupado(null); });
    };
    const firmar = (d: MiDocumento) => {
        if (idPerfil === null) {
            return;
        }
        setOcupado(d.id);
        ops.startMySigningSession(idPerfil, d.id, direccionDeVuelta(window.location.href))
            .then((s) => { navegador.ir(s.url); })
            .catch((e: unknown) => {
                setOcupado(null);
                tratar(e, () => { setNota({ id: d.id, texto: e instanceof ErrorApi && e.estado === 502 ? 'El servicio de firma no responde. No se ha firmado nada. Prueba otra vez en unos minutos.' : 'No hemos podido abrir la firma. Prueba de nuevo.' }); });
            });
    };

    const nombre = perfil?.business.name ?? '';
    const cabecera = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Mis documentos' } as const);
    const lista = docs !== null && docs.perfil === idPerfil ? docs.lista : null;

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus documentos</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setError(false); setIntento((i) => i + 1); }} />
                </section>
            </Pagina>
        );
    }

    return (
        <Pagina cabecera={cabecera} pie={{ tipo: 'reservaria' }} onSalir={onSalir} titulo={{ titulo: 'Mis documentos', sub: 'Lo que firmas en cada negocio. Lo guardamos el tiempo que obliga la ley y puedes descargarlo cuando quieras.' }}>
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select className="rv-pc-select rv-pc-select--ancho" value={idPerfil ?? ''} onChange={(e) => { setElegido(e.target.value); setAbierto(null); }}>
                        {perfiles.map((p) => <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>)}
                    </select>
                </label>
            )}
            <section className="rv-pc-card" aria-busy={lista === null && perfil !== null} aria-label="Tus documentos">
                {perfiles !== null && perfil === null ? (
                    <div className="rv-pc-card__cab"><h2>Todavía no tienes documentos</h2><span>Cuando un negocio te pida firmar algo, lo verás aquí.</span></div>
                ) : lista === null ? (
                    <>
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                    </>
                ) : lista.length === 0 ? (
                    <div className="rv-pc-card__cab"><h2>Todavía no tienes documentos</h2><span>{`Cuando ${nombre} te pida firmar algo, lo verás aquí.`}</span></div>
                ) : (
                    lista.map((d) => {
                        const e = ESTADO[d.status];
                        const abre = abierto === d.id;
                        return (
                            <div key={d.id} className="rv-fd-fila">
                                <button type="button" className="rv-fd-fila__cab" aria-expanded={abre} onClick={() => { setAbierto(abre ? null : d.id); }}>
                                    <span aria-hidden="true" className="rv-fd-fila__baldosa" style={{ background: e.fondo, color: e.tinta }}><Icono nombre={e.icono} /></span>
                                    <span className="rv-fd-fila__texto">
                                        <strong>{d.title}</strong>
                                        <span>{linea(d)}</span>
                                        {d.status !== 'signed' && <Chip estado={e.estado}>{e.chip}</Chip>}
                                    </span>
                                    <Icono nombre={abre ? 'chevron-up' : 'chevron-down'} />
                                </button>
                                {abre && (
                                    <div className="rv-fd-fila__cuerpo">
                                        {d.status === 'signed' && (
                                            <>
                                                <div className="rv-pc-fila"><span>Firmado</span><strong>{fecha(d.signed_at)}</strong></div>
                                                <div className="rv-pc-fila"><span>Vale</span><strong>{d.valid_until == null ? 'Solo para esa cita' : `Hasta el ${fecha(d.valid_until)}: no te lo volvemos a pedir`}</strong></div>
                                                <BotonPc tipo="sec" texto="Descargar el PDF firmado" icono="download" ocupado={ocupado === d.id} ocupadoTexto="Preparando…" onClick={() => { descargar(d); }} />
                                            </>
                                        )}
                                        {d.status === 'requested' && (
                                            <>
                                                <div className="rv-pc-fila"><span>Plazo</span><strong>{`Hasta el ${plazo(d.sign_by)}`}</strong></div>
                                                {d.can_sign && <BotonPc texto="Leer y firmar" icono="pen-line" ocupado={ocupado === d.id} ocupadoTexto="Abriendo…" onClick={() => { firmar(d); }} />}
                                            </>
                                        )}
                                        {d.status === 'pending' && <p className="rv-pc-nota"><Icono nombre="store" /><span>Pasó el plazo para firmarlo desde el móvil. Lo firmas en el centro al llegar; la cita no se cancela.</span></p>}
                                        {d.status === 'voided' && <p className="rv-pc-nota"><Icono nombre="calendar-x" /><span>Se anuló porque la cita se canceló. No tienes que firmarlo.</span></p>}
                                        {nota?.id === d.id && <p className="rv-pc-nota" role="status"><Icono nombre="clock" /><span>{nota.texto}</span></p>}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </section>
        </Pagina>
    );
}
